import { useEffect, useState, useCallback } from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import "./App.css";
import { Toaster } from "./components/ui/sonner";
import { toast } from "sonner";
import { api, saveSession, loadSession, clearSession } from "./lib/api";
import { useRoomPolling } from "./lib/useRoom";

import Home from "./screens/Home";
import Lobby from "./screens/Lobby";
import Game from "./screens/Game";
import GameOFA from "./screens/GameOFA"; // <-- NOWY IMPORT DLA TRYBU KOOPERACJI
import Scoreboard from "./screens/Scoreboard";

import Rules from "./screens/Rules";
import About from "./screens/About";
import Privacy from "./screens/Privacy";
import Contact from "./screens/Contact";

import { LanguageProvider, LanguageSwitcher } from "./lib/i18n";
import { AuthProvider, AccountWidget } from "./lib/auth";

function App() {
  const [session, setSession] = useState(() => loadSession());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const code = session?.code;
  const playerId = session?.player_id;
  const { room, setRoom } = useRoomPolling(code, playerId, !!code);

  useEffect(() => {
    if (!code) return;
    let mounted = true;
    api.getRoom(code, playerId).catch(() => {
      if (mounted) {
        clearSession();
        setSession(null);
      }
    });
    return () => {
      mounted = false;
    };
  }, [code, playerId]);

  const enter = (data) => {
    const s = { code: data.room.code, player_id: data.player_id };
    saveSession(s);
    setSession(s);
    setRoom(data.room);
  };

  const onCreate = async (name) => {
    setLoading(true);
    setError("");
    try {
      enter(await api.createRoom(name));
    } catch (e) {
      setError(e.response?.data?.detail || "Could not create room");
    } finally {
      setLoading(false);
    }
  };

  const onJoin = async (roomCode, name) => {
    setLoading(true);
    setError("");
    try {
      enter(await api.joinRoom(roomCode, name));
    } catch (e) {
      setError(e.response?.data?.detail || "Could not join room");
    } finally {
      setLoading(false);
    }
  };

  const leave = useCallback(async () => {
    if (code && playerId) {
      try {
        await api.leave(code, playerId);
      } catch (e) {
        console.warn("Leave request failed:", e?.message || e);
      }
    }
    clearSession();
    setSession(null);
    setRoom(null);
  }, [code, playerId, setRoom]);

  const wrap = (promise, errMsg) =>
    promise
      .then((d) => {
        setRoom(d.room);
        return d;
      })
      .catch((e) => {
        toast.error(e.response?.data?.detail || errMsg || "Something went wrong");
        throw e;
      });

  const actions = {
    vote: (set_id) => wrap(api.vote(code, playerId, set_id)),
    addSet: (name, cards) => wrap(api.addSet(code, name, cards), "Failed to add set"),
    config: (cfg) => wrap(api.config(code, playerId, cfg)),
    start: () => wrap(api.start(code, playerId), "Failed to start"),
    guess: (g) => wrap(api.guess(code, playerId, g)),
    question: () => wrap(api.question(code, playerId)),
    confirm: (target_id) => wrap(api.confirm(code, playerId, target_id)),
    skip: () => wrap(api.skip(code, playerId)),
    end: () => wrap(api.end(code, playerId)),
    restart: () => wrap(api.restart(code, playerId)),
    updateRoom: (r) => setRoom(r),
    leave,
  };

  // Ustalanie ekranu gry
  let screen;
  if (!session || !room) {
    screen = <Home onCreate={onCreate} onJoin={onJoin} loading={loading} error={error} />;
  } else if (room.status === "lobby") {
    screen = <Lobby room={room} playerId={playerId} actions={actions} />;
  } else if (room.status === "playing" || room.status === "ended") {
    // --- NOWA LOGIKA ROZDZIELAJĄCA TRYBY ---
    if (room.mode === "ofa") {
      screen = <GameOFA room={room} playerId={playerId} actions={actions} />;
    } else {
      screen = <Game room={room} playerId={playerId} actions={actions} />;
    }
  } else {
    screen = <Scoreboard room={room} playerId={playerId} actions={actions} />;
  }

  return (
    <Router>
      <LanguageProvider>
        <AuthProvider>
          <div className="App">
            <Routes>
              {/* Główna ścieżka gry */}
              <Route path="/" element={screen} />
              
              {/* Podstrony pod SEO */}
              <Route path="/zasady" element={<Rules />} />
              <Route path="/o-nas" element={<About />} />
              <Route path="/polityka-prywatnosci" element={<Privacy />} />
              <Route path="/kontakt" element={<Contact />} />
            </Routes>

            <LanguageSwitcher />
            <AccountWidget />
            <Toaster position="top-center" theme="dark" richColors />
          </div>
        </AuthProvider>
      </LanguageProvider>
    </Router>
  );
}

export default App;